/**
 * Monaco editor completion provider for <fm:*> component tags.
 *
 * Provides:
 * 1. Tag completion — typing `<fm:` suggests all component names
 * 2. Attribute completion — inside an open tag, suggests component-specific attributes
 * 3. Attribute value completion — for attributes with enumerated values
 * 4. Variable completion — typing `{$` suggests available template variables
 */
import * as monaco from 'monaco-editor'
import { fmComponents, fmComponentMap, type FMComponentDef } from './fmComponentRegistry'

/**
 * Determine which <fm:Component> tag the cursor is currently inside, if any.
 */
function getEnclosingFmTag(
	model: monaco.editor.ITextModel,
	position: monaco.Position
): { componentName: string; tagContent: string } | null {
	// Scan backwards from cursor to find the most recent `<fm:` that isn't closed
	const textUntilCursor = model.getValueInRange({
		startLineNumber: 1,
		startColumn: 1,
		endLineNumber: position.lineNumber,
		endColumn: position.column,
	})

	// Find the last unclosed <fm: tag
	let depth = 0
	let lastOpenTag = ''
	let lastOpenContent = ''

	// Regex to find <fm: tags and their closing > or />
	const tagRegex = /<fm:([A-Za-z:]+)([^>]*?)(\/?>)|<\/fm:([A-Za-z:]+)\s*>/g
	let match
	let lastUnclosedStart = -1

	// Simple approach: find the last `<fm:...` that doesn't have a matching `>`
	const lastLt = textUntilCursor.lastIndexOf('<fm:')
	if (lastLt === -1) return null

	const afterLt = textUntilCursor.substring(lastLt)
	// Check if this tag is still "open" (no closing > yet)
	const closingBracket = afterLt.indexOf('>')
	if (closingBracket !== -1) {
		// The tag is already closed — cursor is outside
		return null
	}

	// Extract the component name
	const nameMatch = afterLt.match(/^<fm:([A-Za-z:]+)/)
	if (!nameMatch) return null

	return {
		componentName: nameMatch[1],
		tagContent: afterLt,
	}
}

/**
 * Check if cursor is inside an attribute value (between quotes).
 */
function getAttributeContext(tagContent: string): { attributeName: string } | null {
	// Find if we're inside name="..." — look for the last attribute assignment
	// Pattern: name="...  (cursor is after the opening quote)
	const attrMatch = tagContent.match(/(\w+)=["'][^"']*$/)
	if (attrMatch) {
		return { attributeName: attrMatch[1] }
	}
	return null
}

/**
 * Build the completion provider for <fm:*> component autocomplete.
 */
export function createFmCompletionProvider() {
	return {
		triggerCharacters: ['<', ':', ' ', '"', "'", '{', '$'],
		provideCompletionItems(
			model: monaco.editor.ITextModel,
			position: monaco.Position,
			context: monaco.languages.CompletionContext,
			token: monaco.CancellationToken
		): monaco.languages.ProviderResult<monaco.languages.CompletionList> {
			const textUntilPosition = model.getValueInRange({
				startLineNumber: position.lineNumber,
				startColumn: 1,
				endLineNumber: position.lineNumber,
				endColumn: position.column,
			})

			const fullTextUntil = model.getValueInRange({
				startLineNumber: 1,
				startColumn: 1,
				endLineNumber: position.lineNumber,
				endColumn: position.column,
			})

			const word = model.getWordUntilPosition(position)
			const range = {
				startLineNumber: position.lineNumber,
				endLineNumber: position.lineNumber,
				startColumn: word.startColumn,
				endColumn: word.endColumn,
			}

			// ─── 1. Tag completion: <fm: ───
			if (textUntilPosition.match(/<fm:[\w:]*$/)) {
				// Extract partial typed name after <fm:
				const partialMatch = textUntilPosition.match(/<fm:([\w:]*)$/)
				const partial = partialMatch ? partialMatch[1].toLowerCase() : ''

				const tagRange = {
					startLineNumber: position.lineNumber,
					endLineNumber: position.lineNumber,
					startColumn: position.column - (partial?.length || 0),
					endColumn: position.column,
				}

				const suggestions: monaco.languages.CompletionItem[] = fmComponents
					.filter(c => c.name.toLowerCase().startsWith(partial))
					.map((comp, i) => {
						const isSelfClosing = comp.selfClosing
						const requiredAttrs = comp.attributes.filter(a => a.required)
						let insertSnippet = comp.name

						if (requiredAttrs.length > 0) {
							const attrStr = requiredAttrs.map((a, idx) =>
								`${a.name}="\${${idx + 1}:${a.default || ''}}"`
							).join(' ')
							insertSnippet += ' ' + attrStr
						}

						if (isSelfClosing) {
							insertSnippet += ' />'
						} else {
							insertSnippet += `>\n\t\$0\n</fm:${comp.name}>`
						}

						return {
							label: `fm:${comp.name}`,
							kind: monaco.languages.CompletionItemKind.Class,
							detail: comp.category,
							documentation: {
								value: `**${comp.name}** — ${comp.description}\n\n` +
									(comp.attributes.length > 0
										? '**Attributes:** ' + comp.attributes.map(a =>
											`\`${a.name}\`` + (a.required ? ' *(required)*' : '')
										).join(', ')
										: 'No attributes') +
									(comp.variables && comp.variables.length > 0
										? '\n\n**Variables:** ' + comp.variables.map(v =>
											`\`{$${v.name}}\` — ${v.description}`
										).join(', ')
										: ''),
							},
							insertText: insertSnippet,
							insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
							range: tagRange,
							sortText: String(i).padStart(4, '0'),
						}
					})

				return { suggestions }
			}

			// ─── 2. Variable completion: {$ or {$$ ───
			if (textUntilPosition.match(/\{\$\$?[\w.]*$/)) {
				const partialMatch = textUntilPosition.match(/\{\$\$?([\w.]*)$/)
				const partial = partialMatch ? partialMatch[1].toLowerCase() : ''

				const varRange = {
					startLineNumber: position.lineNumber,
					endLineNumber: position.lineNumber,
					startColumn: position.column - (partial?.length || 0),
					endColumn: position.column,
				}

				// Collect variables from the enclosing component context
				const enclosing = getEnclosingComponentFromFullText(fullTextUntil)
				const allVars: { name: string; description: string }[] = []

				// Global template variables
				allVars.push(
					{ name: 'loggedinuser', description: 'Current logged-in user ID' },
					{ name: 'vhostid', description: 'Current vhost ID' },
					{ name: 'appid', description: 'Current application ID' },
					{ name: 'servername', description: 'Server hostname' },
					{ name: 'requesturi', description: 'Current request URI' },
				)

				// Variables from enclosing components
				if (enclosing) {
					const comp = fmComponentMap.get(enclosing)
					if (comp?.variables) {
						allVars.push(...comp.variables)
					}
				}

				const suggestions: monaco.languages.CompletionItem[] = allVars
					.filter(v => v.name.toLowerCase().includes(partial))
					.map((v, i) => ({
						label: `{$$${v.name}}`,
						kind: monaco.languages.CompletionItemKind.Variable,
						detail: v.description,
						insertText: v.name + '}',
						range: varRange,
						sortText: String(i).padStart(4, '0'),
					}))

				return { suggestions }
			}

			// ─── 3. Attribute completion inside an open <fm: tag ───
			const enclosingTag = getEnclosingFmTag(model, position)
			if (enclosingTag) {
				const comp = fmComponentMap.get(enclosingTag.componentName)
				if (!comp) return { suggestions: [] }

				// Check if we're inside an attribute value
				const attrCtx = getAttributeContext(enclosingTag.tagContent)
				if (attrCtx) {
					// ─── 3b. Attribute value completion ───
					const attr = comp.attributes.find(a => a.name === attrCtx.attributeName)
					if (attr?.values) {
						return {
							suggestions: attr.values.map((val, i) => ({
								label: val,
								kind: monaco.languages.CompletionItemKind.Value,
								detail: `Value for ${attr.name}`,
								insertText: val,
								range,
								sortText: String(i).padStart(4, '0'),
							})),
						}
					}
					return { suggestions: [] }
				}

				// ─── 3a. Attribute name completion ───
				// Figure out which attributes are already used
				const usedAttrs = new Set<string>()
				const attrRegex = /(\w+)\s*=/g
				let m
				while ((m = attrRegex.exec(enclosingTag.tagContent)) !== null) {
					usedAttrs.add(m[1])
				}

				const suggestions: monaco.languages.CompletionItem[] = comp.attributes
					.filter(a => !usedAttrs.has(a.name))
					.map((attr, i) => ({
						label: attr.name,
						kind: monaco.languages.CompletionItemKind.Property,
						detail: attr.type ? `(${attr.type})` + (attr.required ? ' *required*' : '') : '',
						documentation: {
							value: attr.description +
								(attr.default ? `\n\nDefault: \`${attr.default}\`` : '') +
								(attr.values ? `\n\nValues: ${attr.values.map(v => `\`${v}\``).join(', ')}` : ''),
						},
						insertText: `${attr.name}="\${1:${attr.default || ''}}"`,
						insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
						range,
						sortText: (attr.required ? '0' : '1') + String(i).padStart(4, '0'),
					}))

				return { suggestions }
			}

			return { suggestions: [] }
		},
	}
}

/**
 * Find the nearest enclosing <fm:Component> from the full text up to cursor.
 * Used for variable context resolution.
 */
function getEnclosingComponentFromFullText(text: string): string | null {
	const stack: string[] = []
	const tagRegex = /<fm:([A-Za-z:]+)[^>]*?(\/?)>|<\/fm:([A-Za-z:]+)\s*>/g
	let match

	while ((match = tagRegex.exec(text)) !== null) {
		if (match[3]) {
			// Closing tag
			if (stack.length > 0 && stack[stack.length - 1] === match[3]) {
				stack.pop()
			}
		} else if (match[2] === '/') {
			// Self-closing — doesn't affect stack
		} else {
			// Opening tag
			stack.push(match[1])
		}
	}

	return stack.length > 0 ? stack[stack.length - 1] : null
}
